import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddCaseDialog = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  
  // Form state - منفصل تماماً عن الـ parent component
  const [title, setTitle] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [caseType, setCaseType] = useState("civil");
  const [status, setStatus] = useState("active");
  const [courtName, setCourtName] = useState("");
  const [judgeName, setJudgeName] = useState("");
  const [myPartyLawyer, setMyPartyLawyer] = useState("");
  const [plaintiff, setPlaintiff] = useState("");
  const [defendant, setDefendant] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setTitle("");
    setCaseNumber("");
    setCaseType("civil");
    setStatus("active");
    setCourtName("");
    setJudgeName("");
    setMyPartyLawyer("");
    setPlaintiff("");
    setDefendant("");
    setDescription("");
    setNotes("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title,
        case_number: caseNumber,
        case_type: caseType,
        status,
        court_name: courtName,
        judge_name: judgeName,
        my_party_lawyer: myPartyLawyer,
        plaintiff,
        defendant,
        description,
        notes
      };

      await axios.post(`${API}/cases`, payload);
      toast.success("تم إضافة القضية بنجاح");
      setOpen(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("خطأ في إضافة القضية");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-case-button">
          <Plus className="w-5 h-5" />
          قضية جديدة
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-case-dialog">
        <DialogHeader>
          <DialogTitle>إضافة قضية جديدة</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">عنوان القضية *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                data-testid="case-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="case_number">رقم القضية</Label>
              <Input
                id="case_number"
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
                data-testid="case-number-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="case_type">نوع القضية *</Label>
              <Select value={caseType} onValueChange={setCaseType}>
                <SelectTrigger id="case_type" data-testid="case-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="civil">مدني</SelectItem>
                  <SelectItem value="criminal">جنائي</SelectItem>
                  <SelectItem value="commercial">تجاري</SelectItem>
                  <SelectItem value="labor">عمل</SelectItem>
                  <SelectItem value="personal_status">أحوال شخصية</SelectItem>
                  <SelectItem value="administrative">إداري</SelectItem>
                  <SelectItem value="employee_judiciary">قضاء موظفين</SelectItem>
                  <SelectItem value="investigation">تحقيق</SelectItem>
                  <SelectItem value="misdemeanor">جنح</SelectItem>
                  <SelectItem value="internal_security">قوى الأمن الداخلي</SelectItem>
                  <SelectItem value="military_court">المحكمة العسكرية</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">حالة القضية *</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status" data-testid="case-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">نشطة</SelectItem>
                  <SelectItem value="pending">قيد الانتظار</SelectItem>
                  <SelectItem value="closed">مغلقة</SelectItem>
                  <SelectItem value="archived">مؤرشفة</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="court_name">اسم المحكمة</Label>
            <Input
              id="court_name"
              value={courtName}
              onChange={(e) => setCourtName(e.target.value)}
              data-testid="court-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="judge_name">اسم القاضي (اختياري)</Label>
              <Input
                id="judge_name"
                placeholder="مثال: القاضي أحمد محمد"
                value={judgeName}
                onChange={(e) => setJudgeName(e.target.value)}
                data-testid="judge-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="my_party_lawyer">المحامي (اختياري)</Label>
              <Input
                id="my_party_lawyer"
                placeholder="مثال: المحامي علي حسن"
                value={myPartyLawyer}
                onChange={(e) => setMyPartyLawyer(e.target.value)}
                data-testid="my-party-lawyer-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="plaintiff">المدعي</Label>
              <Input
                id="plaintiff"
                value={plaintiff}
                onChange={(e) => setPlaintiff(e.target.value)}
                data-testid="plaintiff-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="defendant">المدعى عليه</Label>
              <Input
                id="defendant"
                value={defendant}
                onChange={(e) => setDefendant(e.target.value)}
                data-testid="defendant-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">وصف القضية *</Label>
            <Textarea
              id="description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              data-testid="description-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات</Label>
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="notes-input"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              إلغاء
            </Button>
            <Button type="submit" data-testid="submit-case-button">حفظ القضية</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCaseDialog;
